/**
 * Hook: useEditUserProfile
 *
 * Manages all form state and business logic for editing a user profile.
 * Matches Flutter's EditUserProfileScreenState logic exactly.
 */

import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { ConfirmSheet } from '@/presentation/components/common/sheet/confirm/ConfirmSheet';
import { bustImageCache } from '@/presentation/components/common/CachedImage';
import { Navigator } from '@/presentation/routing/Navigator';
import { UserUtils } from '@/domain/utils/userUtils';
import { editUserProfileUseCase } from '@/domain/usecases/user/editUserProfileUseCase';
import { refreshAuthUserUseCase } from '@/domain/usecases/user/refreshAuthUserUseCase';
import { generateUsernameUseCase } from '@/domain/usecases/user/generateUsernameUseCase';
import { usernameExistsUseCase } from '@/domain/usecases/user/usernameExistsUseCase';
import type { RootState } from '@/presentation/store';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import type { OnboardInfoInput } from '@/domain/input/user/onboardInfoInput';
import { remoteConfigService } from '@/domain/services/remoteConfig/remoteConfigService';
import { TextInputFormatters } from '@/presentation/constants/formatters/textInputFormatters';

export interface UseEditUserProfileResult {
  /**
   * The avatar to render: a full profiles-bucket read URL for an already-uploaded
   * image, a `blob:` object URL for a freshly-picked local file, or null.
   * Port of Flutter's `_profileImage` (edit_user_profile_screen.dart:58).
   */
  profileImage: string | null;
  name: string;
  username: string;
  bio: string;
  subscriptionCharge: string;
  isGeneratingUsername: boolean;
  usernameExists: boolean;
  isSaving: boolean;
  canSave: boolean;
  setName: (value: string) => void;
  setUsername: (value: string) => void;
  setBio: (value: string) => void;
  setSubscriptionCharge: (value: string) => void;
  onNameBlur: () => Promise<void>;
  onUsernameBlur: () => Promise<void>;
  /**
   * Accepts a locally-picked image file and stages it for the next save.
   * Port of Flutter's `onChangeImageTapped` result branch
   * (edit_user_profile_screen.dart:280-285). The caller (the page) owns opening
   * the picker sheet; this only records the outcome.
   */
  onProfileImagePicked: (file: File) => void;
  handleSubmit: () => Promise<void>;
}

export function useEditUserProfile(screenKey?: string): UseEditUserProfileResult {
  const { l10n } = useLocale();
  const queryClient = useQueryClient();
  const currentUser = useSelector((state: RootState) => state.auth.user);

  // Port of `edit_user_profile_screen.dart:100-103`: an already-uploaded avatar
  // seeds with the FULL read URL, not the stored short form. That is load-bearing —
  // the full URL still contains the profiles-bucket name, so the use case's
  // `changed` predicate (users_repository.dart:65-67) reads "unchanged" and skips
  // the upload when the user never touches the image.
  const [profileImage, setProfileImageState] = useState<string | null>(() => {
    if (!currentUser) return null;
    return UserUtils.isProfileImageUploaded(currentUser)
      ? UserUtils.generateUserProfileImageUrl(currentUser.firebaseId)
      : currentUser.profileImage ?? null;
  });

  const [name, setNameState] = useState(currentUser?.name ?? '');
  const [username, setUsernameState] = useState(currentUser?.username ?? '');
  const [bio, setBioState] = useState(currentUser?.bio ?? '');
  const [subscriptionCharge, setSubscriptionChargeState] = useState(
    currentUser?.subscriptionCharge != null
      ? String(currentUser.subscriptionCharge)
      : '0'
  );
  const [isGeneratingUsername, setIsGeneratingUsername] = useState(false);
  const [usernameExists, setUsernameExists] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Tracks whether any data was changed (like Flutter's _dataUpdated)
  const dataUpdatedRef = useRef(false);
  // Tracks whether identity-sensitive data changed (name, username, profile image
  // — like Flutter's _identityDataUpdated). WRITE-ONLY today: `setName`, `setUsername` and
  // `onProfileImagePicked` set it and nothing reads it, because its only consumer — Flutter's
  // identity-verification invalidation (`_onSaveTapped`, edit_user_profile_screen.dart:342-383) —
  // is not ported. Kept so the follow-up marked in `handleSubmit` has its triggers already wired.
  // Do NOT delete it as dead code.
  const identityDataUpdatedRef = useRef(false);

  // Holds the `blob:` object URL of the currently-picked image so it can be revoked.
  // Web-only housekeeping with no Flutter analog (Flutter holds a file path, not a
  // browser resource handle); without it every pick leaks a blob for the tab's life.
  const objectUrlRef = useRef<string | null>(null);

  useEffect(
    () => () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    },
    []
  );

  /**
   * Flutter's `_ageChecked` (edit_user_profile_screen.dart:62), folded into
   * `_canSave()` at `:82`. Flutter initialises it `_ageChecked = _user.onboarded`
   * (`:97`) under its own comment (`:94-96`): "Always true now as onboarding screen
   * is not displayed after each login anymore… so age has already been checked".
   *
   * Kept per task-prompt line 6 ("_ageChecked is always true. Keep the dummy flag
   * though on the Web in case it will be needed in the future") rather than dropped.
   *
   * Deliberately a literal `true`, NOT `currentUser.onboarded` (`:97`): its `canSave`
   * contribution is then a provable no-op, whereas deriving it would create a
   * web-only save lockout — the web has no onboarding screen, and Flutter's age
   * checkbox (`_confirmAgeWidget`, the only control that flips the flag) is
   * `visible: !widget.isEditing` (`:1028`), i.e. never reachable on web.
   *
   * Not ported for the same reason: `_onSaveTapped`'s `!_ageChecked` "Age validation"
   * sheet (`:306-317`) is unreachable by construction under this invariant.
   */
  const ageChecked = true;

  const subscriptionChargeNumber = useMemo(
    () => parseFloat(subscriptionCharge.replace(',', '.')),
    [subscriptionCharge]
  );
  const canSave =
    name.trim().length > 0 &&
    username.trim().length > 0 &&
    ageChecked &&
    !usernameExists &&
    // Flutter: `RemoteConfigService.exclusiveSubscriptionMinDollars` /
    // `.exclusiveSubscriptionMaxDollars` (edit_user_profile_screen.dart:328-335).
    TextInputFormatters.isValidDecimalAmount(
      subscriptionCharge,
      remoteConfigService.exclusiveSubscriptionMinDollars,
      remoteConfigService.exclusiveSubscriptionMaxDollars
    );

  const setName = useCallback((value: string) => {
    identityDataUpdatedRef.current = true;
    dataUpdatedRef.current = true;
    setNameState(value);
  }, []);

  const setUsername = useCallback((value: string) => {
    identityDataUpdatedRef.current = true;
    dataUpdatedRef.current = true;
    const lowered = value.toLowerCase();
    setUsernameState(lowered);
    if (lowered.trim().length === 0) {
      setUsernameExists(true);
    }
  }, []);

  const setBio = useCallback((value: string) => {
    dataUpdatedRef.current = true;
    setBioState(value);
  }, []);

  const setSubscriptionCharge = useCallback((value: string) => {
    dataUpdatedRef.current = true;
    setSubscriptionChargeState(value);
  }, []);

  // Port of `onChangeImageTapped`'s result branch (edit_user_profile_screen.dart:280-285).
  // The picker itself (`:272-279`) is the page's job — a hook never opens a sheet.
  //
  // No `hideKeyboard()` port (`:271`, `:288-292`): clicking the avatar moves focus off
  // the active input, so React's onBlur fires and the existing onNameBlur/onUsernameBlur
  // run exactly as Flutter's focus listeners (`:164-185`) do. There is no mobile keyboard
  // to dismiss, so parity holds by default.
  const onProfileImagePicked = useCallback((file: File) => {
    // Replacing an earlier pick — release the previous blob first.
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);

    const url = URL.createObjectURL(file);
    objectUrlRef.current = url;

    identityDataUpdatedRef.current = true; // `:281`
    // `:282` — mandatory: without it handleSubmit's no-change early-return would
    // silently discard the picked image.
    dataUpdatedRef.current = true;
    setProfileImageState(url); // `:283-284`
  }, []);

  // Auto-generate username if empty when name field loses focus (matches Flutter onNameFocusChange)
  const onNameBlur = useCallback(async () => {
    if (username.trim().length === 0 && name.trim().length > 0) {
      setIsGeneratingUsername(true);
      try {
        const generated = await generateUsernameUseCase.execute(name);
        setUsernameState(generated);
        setUsernameExists(false);
      } finally {
        setIsGeneratingUsername(false);
      }
    }
  }, [name, username]);

  // Check username availability when username field loses focus (matches Flutter onUsernameFocusChange)
  const onUsernameBlur = useCallback(async () => {
    if (username.trim().length === 0) return;
    setIsGeneratingUsername(true);
    try {
      const exists = await usernameExistsUseCase.execute(
        username,
        currentUser?.firebaseId
      );
      setUsernameExists(exists);
    } finally {
      setIsGeneratingUsername(false);
    }
  }, [username]);

  // TODO: @claude add a follow up task for this: layer-ownership violation — this is a PAGE feature
  // hook (`feature/[feature]/hooks/use[Feature].ts`), so it must not call `Navigator.*` or fire
  // `ConfirmSheet.show`; it should expose memoized callbacks the page wires (`presentation.md`
  // §"Page + feature-hook split"; `workflow/lessons.md` §Layer ownership). The five offending call
  // sites all sit inside `handleSubmit` below: the no-change `Navigator.goBack()` early-return, the
  // limit-exceeded / validation / save-error `ConfirmSheet.show`, and the terminal
  // `Navigator.goBack()`. Pre-existing on main, NOT introduced by feat_edit_profile_image_upload.
  // The fix hoists them into page-wired callbacks (onNoChanges / onLimitExceeded /
  // onValidationError / onSaveError / onSaveComplete) threaded through EditUserProfilePage, and
  // must preserve handleSubmit's early-return ordering against Flutter's `_onSaveTapped`
  // (lib/presentation/screens/user/edit_user_profile_screen.dart:294-303).
  //
  // Not a drive-by, but bounded: this is the LAST live PAGE-hook offender — a sweep of
  // `feature/**/hooks/` found no siblings. `useVideoCall` (injected `goBack` prop) and `useRoadmap`
  // (`pendingDeepLinkOpen` intent the page consumes) are already remediated and are the templates
  // to copy; the `*SheetSurface` hooks (`useContactSheetSurface`, `useAddRoadmapSheetSurface`) are
  // the rule's sanctioned carve-out, not offenders.
  // See workflow/code_reviews/feat_edit_profile_image_upload_code_review/finding_3.md.
  //
  // Main save handler — mirrors Flutter's _onSaveTapped + _save
  const handleSubmit = useCallback(async () => {
    if (isSaving) return;

    // If nothing changed, just go back (matches Flutter's isEditing && !_dataUpdated check)
    if (!dataUpdatedRef.current) {
      Navigator.goBack();
      return;
    }

    if (!canSave) {
      // Validated inside the page on user input and also by `canSave`,
      // but double-check before calling use case
      //
      // Flutter: `RemoteConfigService.exclusiveSubscriptionMinDollars` /
      // `.exclusiveSubscriptionMaxDollars` (edit_user_profile_screen.dart:701-702).
      if (!TextInputFormatters.isValidDecimalAmount(
        subscriptionCharge,
        remoteConfigService.exclusiveSubscriptionMinDollars,
        remoteConfigService.exclusiveSubscriptionMaxDollars
      )) {
        await ConfirmSheet.show({
          title: l10n.editUserProfile.limitExceededTitle,
          confirmText: l10n.editUserProfile.limitExceededDescription,
          okText: l10n.editUserProfile.errorOk,
          screenKey,
        });
        return;
      }

      // Missing required fields or username already exists
      await ConfirmSheet.show({
        title: l10n.editUserProfile.validationTitle,
        confirmText: l10n.editUserProfile.validationDescription,
        okText: l10n.editUserProfile.errorOk,
        screenKey,
      });
      return;
    }

    // TODO: @claude add a follow up task for this: identity-verification invalidation on identity-data
    // change is NOT ported — Flutter lib/presentation/screens/user/edit_user_profile_screen.dart:342-383.
    // Two halves, both missing; the SECOND is the load-bearing one:
    //   (1) the confirm sheet warning "your Identity Verification will be inactivated and you will lose
    //       your Identity Blue badge" (`:352-365`), gated on the identity-verification status being
    //       approved / inReview / requested (`:345-351`);
    //   (2) `identityVerificationRepository.decline(requestId, IdentityVerificationDeclineReason
    //       .identityDataChanged)` (`:375-378`, behind a request-id null check at `:374`) — the write
    //       that actually REVOKES the badge. Shipping only (1) leaves the badge live and does NOT
    //       close the gap.
    // Runtime gap today: a web user with an approved verification can change their profile photo — the
    // very thing the badge attests ("this photo is this person") — and keep the badge. Flutter warns,
    // then revokes.
    // Scope note (why this is a marker, not a drive-by): the gate is cheap — `IdentityVerificationStatus`,
    // `IdentityVerificationDeclineReason.identityDataChanged` and `selectGetIdentityVerificationStatus`
    // (presentation/store/slices/premiumPackages/premiumPackagesSlice.ts) all exist. The revocation is
    // not: the web has NO decline write path (no `IdentityVerificationRequestsRepository` equivalent
    // under data/ or domain/) and the premiumPackages slice exposes no request-id selector (Flutter's
    // `getIdentityVerificationReuqestId()`, `:370-373`). The follow-up must add BOTH.
    // Trigger coverage: the name/username triggers (`setName` / `setUsername`) are pre-existing on main;
    // feat_edit_profile_image_upload ADDED the profile-image trigger in `onProfileImagePicked` (porting
    // Flutter `:281`), which is what makes the gap acute.

    setIsSaving(true);

    // `_save`'s `:391` passes the SCREEN's `_profileImage`, so a picked `blob:` URL
    // reaches the use case and triggers the upload. Passing the untouched
    // `currentUser.profileImage` here would discard every pick.
    const onboardInfo: OnboardInfoInput = {
      profileImage,
      name: name.trim(),
      username: username.trim(),
      bio: bio.trim() || null,
      subscriptionCharge: subscriptionChargeNumber,
    };

    const { success, profileImageReuploaded } =
      await editUserProfileUseCase.execute(onboardInfo);

    // Port of users_repository.dart:73-75 → cache_utils.dart:42-56 (both URL variants).
    //
    // The avatar lives at a fixed, never-changing path (`profile/{uid}/{uid}`) that is
    // overwritten in place, so after a re-upload the URL is byte-identical to the one
    // already on screen. Flutter fixes that by evicting the bytes it cached, which makes
    // `CachedNetworkImage` re-fetch. `bustImageCache` is the web equivalent, and it must
    // do MORE than evict — see its doc comment: this module caches only metadata, and the
    // upload's `cacheControl: 'no-cache'` forces revalidation only when a request is
    // actually issued, which a mounted `<img>` with an unchanged `src` never does. So the
    // bust also bumps a render-only URL token, which is what genuinely re-renders the
    // avatar on the kept-alive UserDetailsPage underneath this screen.
    //
    // (Do NOT remove the upload's `no-cache` on the strength of this: it is what keeps a
    // plain reload — which renders the untokenised URL — from serving stale bytes.)
    //
    // The encoded-URL variant is busted for Flutter parity only — no web surface renders
    // it (every avatar site uses `generateUserProfileImageUrl`).
    //
    // Two deliberate deviations:
    // (a) Flutter evicts BEFORE the callable (`:73-75` vs `:87`); we bust after.
    //     Forced by web layering — the image cache is a presentation module the domain
    //     cannot import. User-invisible: the page renders a blocking `isSaving` overlay
    //     across the whole await and nothing reads the cache in between.
    // (b) NOT gated on `success`, mirroring Flutter: a failed callable still leaves the
    //     freshly-uploaded bytes at the fixed `profile/{uid}/{uid}` path, so the stale
    //     render must be refreshed regardless.
    if (profileImageReuploaded && currentUser?.firebaseId) {
      await bustImageCache(UserUtils.generateUserProfileImageUrl(currentUser.firebaseId));
      await bustImageCache(
        UserUtils.generateUserProfileEncodedImageUrl(currentUser.firebaseId)
      );
    }

    if (!success) {
      setIsSaving(false);
      await ConfirmSheet.show({
        title: l10n.editUserProfile.saveErrorTitle,
        confirmText: l10n.editUserProfile.saveErrorDescription,
        okText: l10n.editUserProfile.errorOk,
        screenKey,
      });
      return;
    }

    if (currentUser?.firebaseId) {
      // Refresh failures are logged inside the use case; goBack regardless.
      const updatedUser = await refreshAuthUserUseCase.execute(currentUser.firebaseId);

      if (updatedUser) {
        // Push the freshly-refreshed user into any cached UserDetailsPage query
        // for this user. UserDetailsPage reads via getQueryData (non-reactive),
        // but the auth-slice setUser dispatch from AuthUserListener triggers a
        // re-render that picks up the new value.
        queryClient.setQueriesData(
          {
            predicate: (q) =>
              q.queryKey[1] === 'userDetails' &&
              (q.state.data as UserEntity | undefined)?.firebaseId === updatedUser.firebaseId,
          },
          updatedUser,
        );
      }
    }

    Navigator.goBack();
  }, [
    isSaving,
    canSave,
    subscriptionCharge,
    subscriptionChargeNumber,
    profileImage,
    name,
    username,
    bio,
    currentUser,
    l10n,
    screenKey,
    queryClient,
  ]);

  return {
    profileImage,
    name,
    username,
    bio,
    subscriptionCharge,
    isGeneratingUsername,
    usernameExists,
    isSaving,
    canSave,
    setName,
    setUsername,
    setBio,
    setSubscriptionCharge,
    onNameBlur,
    onUsernameBlur,
    onProfileImagePicked,
    handleSubmit,
  };
}
